import { createFileRoute, Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { ArrowRight } from "lucide-react";

import { HeroStage } from "@/components/home/HeroStage";
import { PackTeaser } from "@/components/home/PackTeaser";
import { PriceList } from "@/components/home/PriceList";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";
import { Button } from "@/components/ui/button";
import { useReveal } from "@/hooks/use-reveal";
import { WHATSAPP_URL } from "@/lib/company";
import {
  AD_SPEND_NOTE,
  AGENCY_OFFER,
  AUDIT_OFFER,
  DEVELOPER_OFFER,
  LAUNCH_OFFER,
  FIT_REVIEW_NOTE,
  MONTHLY_TERMS,
} from "@/lib/pricing";

const FAQS = [
  {
    q: "How much does it cost?",
    a: `A ${LAUNCH_OFFER.name} for one listing or project is ${LAUNCH_OFFER.price}, including 30 days of management, and the senior account review is ${AUDIT_OFFER.price}. Monthly management starts at ${AGENCY_OFFER.price.replace("From ", "")} for agencies and ${DEVELOPER_OFFER.price.replace("From ", "")} for developers; several projects or markets are priced by proposal. ${AD_SPEND_NOTE}`,
  },
  {
    q: "What is the free review?",
    a: "You send your website, the listings or project you want to advertise and your monthly budget. Kristian replies in writing within one business day with the service that fits, its price and what he would do first. No access to your ad account is needed at this stage.",
  },
  {
    q: "How do I pay?",
    a: "Online, through Stripe: by card, Apple Pay or Google Pay, from anywhere. Every service has its own pay button beside the free review, and after a free review Kristian also sends the payment link with his reply. Monthly plans renew by card each month; an invoice is available on request.",
  },
  {
    q: "Do you work with agencies or developers?",
    a: "Both. Agencies and brokers usually need a steady flow of buyer and seller enquiries across their listings; developers need launch and sales-phase campaigns for a project. The monthly plans are priced for each.",
  },
  {
    q: "What do you need from us?",
    a: "Partner access to your ad account and Facebook Page, granted in Meta Business Settings in a few minutes, and the photos, renders or floor plans of what you are selling. Passwords are never requested, and your accounts, Page and data stay yours.",
  },
  {
    q: "Are property ads restricted on Meta?",
    a: "In many markets property ads run under Meta's special rules for housing: broader audiences and a minimum radius around any location. Campaigns are built within those rules from the start, so nothing is rejected or paused later.",
  },
  {
    q: "Do we need to get on a call?",
    a: "No. Adkiro works in writing: the review, the plan and every report arrive by email, and questions are answered by email or WhatsApp. Nobody has to find a time that suits Dubai, London and New York.",
  },
  {
    q: "Which markets do you cover?",
    a: "Dubai and the rest of the UAE, Europe and the United States, in English. Local-language copy is reviewed or supplied by you before anything goes live.",
  },
  {
    q: "How long is the contract?",
    a: `One-off services have no contract. ${MONTHLY_TERMS}`,
  },
];

// Bing Webmaster Tools flagged both as too long on 15 Oct 2026 (80 and 212
// characters). Search engines cut a title at about 60 characters and a
// description at about 160, so these stay under both.
const TITLE = "Adkiro | Meta Ads for Property Agencies & Developers in Dubai";
const DESCRIPTION = `Founder-led Facebook and Instagram ads for property agencies, brokers and developers in Dubai, Europe and the US. Reviews at ${AUDIT_OFFER.price}, launches from ${LAUNCH_OFFER.price}.`;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://adkiro.com/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://adkiro.com/logo.png?v=20260907" },
      { property: "og:image:alt", content: "Adkiro" },
      { name: "twitter:image", content: "https://adkiro.com/logo.png?v=20260907" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://adkiro.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Landing,
});

const STEPS = [
  {
    title: "Free review",
    copy: "Send your website, the listings you want to sell and your budget. Kristian replies in writing within one business day.",
  },
  {
    title: "A proposal with a price",
    copy: "Which service fits, what it costs and what happens first. No calls, no pressure.",
  },
  {
    title: "Pay online and start",
    copy: "Card, Apple Pay or Google Pay through Stripe. Work starts as soon as the photos or the access arrive.",
  },
];

const REASONS = [
  {
    title: "The same methods, tried on his own budget",
    copy: "Kristian advertises listings for his own property business in Sofia on Meta. The same methods run your account, and he runs it himself.",
  },
  {
    title: "Serious buyers over form-fillers",
    copy: "The price in the ad, a form that asks what matters, and campaigns optimised for enquiries your team would actually call back.",
  },
  {
    title: "In writing, and yours to keep",
    copy: "Written plans and reports. Your ad account, Page and data stay yours, and you can remove Adkiro's access at any time.",
  },
];

/** A stagger for things that arrive together: each a beat after the one before. */
const after = (seconds: number) => ({ "--d": `${seconds}s` }) as CSSProperties;

/**
 * The homepage. It opens on the stage (the headline, then the work in three
 * scenes; see HeroStage), and everything a buyer needs to decide follows on
 * paper: the prices as one list, how it starts, who runs the account, the
 * questions, and the one next step.
 */
function Landing() {
  useReveal();

  return (
    <div className="min-h-dvh">
      <SiteHeader tone="ink" overlay />

      <main>
        <HeroStage />

        <section id="services" className="scroll-mt-24">
          <div className="mx-auto max-w-7xl px-5 pb-16 pt-20 lg:px-8 lg:pb-24 lg:pt-28">
            <div className="grid gap-4 lg:grid-cols-[18.75rem_1fr] lg:gap-10">
              <p className="section-kicker lg:pt-4" data-reveal>
                Services and prices
              </p>
              <div>
                <h2 className="text-[clamp(2.375rem,4.4vw,4rem)] leading-none" data-reveal>
                  Clear prices, one step to start
                </h2>
                <p
                  className="mt-5 max-w-xl text-[1.06rem] leading-relaxed text-muted-foreground"
                  data-reveal
                  style={after(0.08)}
                >
                  Start with the free review, or pay online if you already know which service you
                  need. {AD_SPEND_NOTE}
                </p>
              </div>
            </div>

            <div className="mt-10 lg:mt-14">
              <PackTeaser />
            </div>
            <div className="mt-8 lg:mt-10">
              <PriceList />
            </div>
            <div className="mt-5 flex flex-wrap justify-between gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <span>{MONTHLY_TERMS}</span>
              <Link
                to="/meta-ads-pricing"
                hash="pay"
                className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
              >
                Ready to start? Pay online
              </Link>
            </div>
          </div>
        </section>

        <section aria-labelledby="steps-heading">
          <div className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-28">
            <div className="grid gap-4 lg:grid-cols-[18.75rem_1fr] lg:gap-10">
              <p className="section-kicker lg:pt-4" data-reveal>
                How it starts
              </p>
              <h2
                id="steps-heading"
                className="text-[clamp(2.375rem,4.4vw,4rem)] leading-none"
                data-reveal
              >
                Three steps, all online
              </h2>
            </div>
            <ol
              className="steps-line mt-10 grid gap-9 md:grid-cols-3 md:gap-12 lg:mt-14"
              data-observe
            >
              {STEPS.map((step, index) => (
                <li key={step.title} className="pt-7" data-reveal style={after(index * 0.15)}>
                  <span className="font-display text-3xl italic text-primary/50" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <h3 className="mt-5 text-3xl leading-tight">{step.title}</h3>
                  <p className="mt-3 max-w-sm leading-relaxed text-muted-foreground">{step.copy}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section data-tone="ink" className="tone-oxblood relative overflow-hidden">
          {/* The emblem, large and faint, as a watermark on the band. */}
          <svg
            viewBox="0 0 320 320"
            aria-hidden="true"
            className="pointer-events-none absolute right-[-6%] top-1/2 w-[min(32rem,62vw)] -translate-y-1/2 opacity-[0.09]"
          >
            <circle cx="160" cy="160" r="148" fill="none" stroke="#f2ecdf" strokeWidth="3" />
            <path d="M160 61 72 160l88 99 88-99-88-99Z" fill="#f2ecdf" />
            <path d="M160 61v198M72 160h176" fill="none" stroke="#7c1921" strokeWidth="3" />
          </svg>
          <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <p className="section-kicker" data-reveal>
              Why Adkiro
            </p>
            <h2
              className="mt-6 max-w-[19em] text-[clamp(2rem,4.1vw,3.6rem)] leading-[1.06]"
              data-reveal
            >
              Run by someone who <em className="text-primary">sells property</em>, and who runs your
              account himself.
            </h2>
            <ul className="mt-12 grid border-t border-border md:grid-cols-3 lg:mt-16">
              {REASONS.map((reason, index) => (
                <li
                  key={reason.title}
                  className="border-b border-border py-7 md:border-b-0 md:py-0 md:pr-8 md:pt-7 md:[&:not(:first-child)]:border-l md:[&:not(:first-child)]:pl-8"
                  data-reveal
                  style={after(index * 0.12)}
                >
                  <h3 className="text-[1.625rem] leading-tight">{reason.title}</h3>
                  <p className="mt-3 text-[0.94rem] leading-relaxed text-muted-foreground">
                    {reason.copy}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="faq" className="scroll-mt-24">
          <div className="mx-auto grid max-w-7xl gap-8 px-5 py-20 lg:grid-cols-[18.75rem_1fr] lg:gap-10 lg:px-8 lg:py-28">
            <div>
              <p className="section-kicker" data-reveal>
                Questions
              </p>
              <h2 className="mt-4 text-[clamp(2.125rem,3.6vw,3.25rem)] leading-[1.02]" data-reveal>
                Questions property teams ask
              </h2>
            </div>
            <div className="qa" data-reveal>
              {FAQS.map((faq) => (
                <details key={faq.q}>
                  <summary>
                    <h3>{faq.q}</h3>
                  </summary>
                  <p>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="audit" data-tone="ink" className="tone-ink scroll-mt-24">
          <div className="mx-auto max-w-7xl px-5 py-24 text-center lg:px-8 lg:py-32">
            <h2 className="text-[clamp(2.75rem,6.4vw,6rem)] leading-[0.98]" data-reveal>
              Start with a <em className="text-primary">free review</em>
            </h2>
            <p
              className="mx-auto mt-6 max-w-lg text-[1.06rem] leading-relaxed text-muted-foreground"
              data-reveal
              style={after(0.1)}
            >
              {FIT_REVIEW_NOTE}
            </p>
            <div
              className="mt-9 flex flex-col items-center justify-center gap-3.5 sm:flex-row"
              data-reveal
              style={after(0.2)}
            >
              <Button variant="brand" size="xl" className="w-full sm:w-auto" asChild>
                <a href="/#audit">
                  Get a free review <ArrowRight className="size-4" />
                </a>
              </Button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hs-ghost w-full sm:w-auto"
              >
                Message on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
